import type { IconType } from "react-icons";
import { FaAws } from "react-icons/fa";
import { DiVisualstudio } from "react-icons/di";
import { VscAzure } from "react-icons/vsc";
import { SiFlutter, SiDart, SiAndroidstudio, SiGit, SiGitlab, SiBitbucket, SiFirebase, SiMongodb, SiPostgresql, SiSqlite, SiTravisci, SiCirrusci, SiCodemagic, SiSelenium, SiPostman, SiTestrail, SiApachejmeter } from "react-icons/si";

/* -------------------------------------------------------------------------- */
/*                                 TRUSTED BY                                 */
/* -------------------------------------------------------------------------- */

export const trustedLogos = [
  { name: "The CaseVilla", src: "/images/logo/casevilla.png" },
  { name: "Colorbar", src: "/images/logo/colorbar.png" },
  { name: "Health & Wellness Shop", src: "/images/logo/health.png" },
  { name: "Necked", src: "/images/logo/necked.png" },
  { name: "Tarohi", src: "/images/logo/tarohi.png" },
  { name: "Toyzhub", src: "/images/logo/toyzhub.png" },
  { name: "Vastralipi", src: "/images/logo/vastralipi_logo.png" },
  { name: "Suruchira", src: "/images/logo/suruchira.png" },
  { name: "Jewelled by Vandana", src: "/images/logo/Jewelled-by-vandana.png" },
];

/* -------------------------------------------------------------------------- */
/*                            TARGET USER SEGMENTS                            */
/* -------------------------------------------------------------------------- */

export type SegmentItem = {
  title: string;
  description: string;
  image: string;
};

export const userSegments: SegmentItem[] = [
  {
    title: "Individual Doctors",
    description:
      "Ideal for individual doctors, our OPD CRM simplifies appointments, patient management, and workflows.",
    image: "/images/appointment/doctor.svg",
  },
  {
    title: "Multi-doctor Clinics",
    description:
      "Ideal for multi-doctor clinics, our OPD CRM streamlines scheduling and management.",
    image: "/images/appointment/health-clinic.svg",
  },
  {
    title: "Healthcare Centers",
    description:
      "Perfect for healthcare centers, our OPD CRM enhances efficiency, patient management, and scheduling.",
    image: "/images/appointment/hospital.svg",
  },
];
/* -------------------------------------------------------------------------- */
/*                            ALL-IN-ONE FEATURES                             */
/* -------------------------------------------------------------------------- */

export type AllInOneFeature = {
  id: string;
  navLabel: string;
  heading: string;
  points: string[];
  image: string;
  alt: string;
};

export const allInOneFeatures: AllInOneFeature[] = [
  {
    id: "digital-prescription",
    navLabel: "Digital Prescription",
    heading: "Write and share smart digital prescriptions, fast.",
    points: [
      "Customized prescription pad for every speciality",
      "Snomed CT and ICD 10 coded medical database",
      "Brand your printed Rx your way",
    ],
    image: "/images/dr-crm/digital-prescription.png",
    alt: "Digital prescriptions interface",
  },
  {
    id: "clinic-management",
    navLabel: "Clinic Management",
    heading: "Manage your clinic effectively & successfully",
    points: [
      "Manage walk-ins & appts in a single queue",
      "Get automated follow up reminders",
      "Easily manage billings & due payments",
    ],
    image: "/images/dr-crm/clinic-management.png",
    alt: "Clinic management interface",
  },
  {
    id: "online-presence",
    navLabel: "Online Presence",
    heading: "Create & manage your online presence",
    points: [
      "Get personalised website for your practice",
      "Manage & optimize your Google Business Profile",
      "Build your profile and get online appointments",
    ],
    image: "/images/dr-crm/online-presence.png",
    alt: "Online presence management interface",
  },
  {
    id: "patient-engagement",
    navLabel: "Patient Engagement",
    heading: "Stay connected with your patients, always",
    points: [
      "Send automated reminders for bookings",
      "Share digital prescriptions with your patients",
      "Broadcast important updates to your patients",
    ],
    image: "/images/dr-crm/patient-engagement.png",
    alt: "Patient engagement interface",
  },
  {
    id: "queue-management",
    navLabel: "Queue Management",
    heading: "Advanced Queue Management for Seamless Patient Flow",
    points: [
      "Optimize patient flow with efficient queue management",
      "Track and manage queues seamlessly for better organization",
      "Ensure smooth check-ins and hassle-free appointments",
    ],
    image: "/images/dr-crm/queue-management.png",
    alt: "Queue management interface",
  },
];

/* -------------------------------------------------------------------------- */
/*                        KEY FEATURES BY USER TYPE                           */
/* -------------------------------------------------------------------------- */

export type KeyFeatureTab = {
  id: string;
  label: string;
  heading: string;
  description: string;
  features: string[];
  image: string;
  alt: string;
};

export const keyFeatureTabs: KeyFeatureTab[] = [
  {
    id: "doctors",
    label: "Doctors",
    heading: "Smart Tools for Efficient Practice Management",
    description:
      "Our doctor appointment booking CRM helps doctors schedule new appointments, manage patients, and send personalized reminders, making clinic operations smoother and more efficient.",
    features: [
      "Create profile",
      "Printed Rx customization",
      "Email & Text Reminders",
      "Set Availability",
      "Reviews",
      "Notifications",
      "Color-Coded Calendar Views",
      "Appointment Activity History",
    ],
    image: "/images/dr-crm/practice-management.png",
    alt: "Practice management interface",
  },
  {
    id: "patients",
    label: "Patients",
    heading: "Hassle-Free Appointments for Better Healthcare Experience",
    description:
      "Seamless doctor appointment booking with real-time updates, reduced wait times, and improved healthcare experience for better patient care and convenience.",
    features: [
      "Message configurations",
      "Teleconsultations",
      "Search Doctors",
      "Book or schedule appointments",
    ],
    image: "/images/dr-crm/healthcare-experience.png",
    alt: "Patient healthcare experience interface",
  },
  {
    id: "staff",
    label: "Staff",
    heading: "Streamlined Operations for Smooth Clinic Workflow",
    description:
      "Simplified operations, queue management, and better coordination for improved efficiency, reduced workload, and smooth clinic administration.",
    features: [
      "Follow up reminders",
      "Manage Due Payments",
      "Detailed Reports",
    ],
    image: "/images/dr-crm/staff-panel.png",
    alt: "Staff panel interface",
  },
];

/* -------------------------------------------------------------------------- */
/*                               INTEGRATIONS                                 */
/* -------------------------------------------------------------------------- */
export type IntegrationItem = {
  title: string;
  description: string;
  image: string;
  alt: string;
};
export const integrations = [
  {
    title: "EHR Integration",
    description:
      "Patients sign up on our doctor booking CRM to find doctors, book appointments, and manage medical records easily for a smooth healthcare experience.",
    image: "/images/appointment/cumputer-setting.svg",
    alt: "EHR Integration",
  },
  {
    title: "Encrypted Messaging",
    description:
      "Patients sign up on our doctor booking CRM to find doctors, book appointments, and manage medical records easily for a smooth healthcare experience.",
    image: "/images/appointment/encrypted.svg",
    alt: "Encrypted Messaging",
  },
  {
    title: "HD Video Consultations",
    description:
      "Patients sign up on our doctor booking CRM to find doctors, book appointments, and manage medical records easily for a smooth healthcare experience.",
    image: "/images/appointment/video-consultations.svg",
    alt: "HD Video Consultations",
  },
  {
    title: "Integrated Billing",
    description:
      "Patients sign up on our doctor booking CRM to find doctors, book appointments, and manage medical records easily for a smooth healthcare experience.",
    image: "/images/appointment/billing.svg",
    alt: "Integrated Billing",
  },
  {
    title: "Pharmacy Integration",
    description:
      "Patients can directly connect with partner pharmacies to fill prescriptions, track medication status, and receive automated refill reminders.",
    image: "/images/appointment/doctor.svg",
    alt: "Pharmacy Integration",
  },
  {
    title: "Insurance Verification & Claims",
    description:
      "Automatically verify patient insurance eligibility in real-time and streamline claims processing with integrated insurance platforms.",
    image: "/images/appointment/support-maintenance.png",
    alt: "Insurance Verification",
  },
];
/* -------------------------------------------------------------------------- */
/*                               HOW IT WORKS                                 */
/* -------------------------------------------------------------------------- */

export type HowItWorksStep = {
  step: string;
  title: string;
  description: string;
  image: string;
  alt: string;
  gradient: string;
};
export const howItWorksSteps: HowItWorksStep[] = [
  {
    step: "01",
    title: "Sign UP",
    description:
      "Patients sign up on our doctor booking CRM to find doctors, book appointments, and manage medical records easily for a smooth healthcare experience.",
    image: "/images/appointment/sing-up.svg",
    alt: "Sign up",
    gradient: "bg-[#FDF7FC]",
  },
  {
    step: "02",
    title: "Search Doctors",
    description:
      "After signing up, patients can find doctors by specialty, location, availability, and reviews. They can check doctor profiles to choose the right one for their needs.",
    image: "/images/appointment/search.svg",
    alt: "Search doctors",
    gradient: "bg-[#F3EDF9]",
  },
  {
    step: "03",
    title: "Book Appointment",
    description:
      "After finding a doctor, patients can pick a time slot and book an appointment. They can also mention the reason for their visit for better care.",
    image: "/images/appointment/book-list.svg",
    alt: "Book appointment",
    gradient: "bg-[#FDEDEF]",
  },
  {
    step: "04",
    title: "Appointment Confirmation",
    description:
      "The doctor reviews the appointment request and confirms the booking. Patients get a notification with appointment details and important instructions.",
    image: "/images/appointment/pc-confirm.svg",
    alt: "Appointment confirmation",
    gradient: "bg-[#F1F8FF]",
  },
  {
    step: "05",
    title: "Visit & Consultation",
    description:
      "Patients visit the doctor at the scheduled time for their consultation. The app sends check-in reminders and provides directions to the clinic for convenience.",
    image: "/images/appointment/doctor-visit.svg",
    alt: "Visit consultation",
    gradient: "bg-[#F2F7FD]",
  },
  {
    step: "06",
    title: "Rating & Feedback",
    description:
      "After the appointment, patients can share their feedback and rate the doctor's service, helping others make better healthcare choices.",
    image: "/images/appointment/three-star-thum.svg",
    alt: "Rating feedback",
    gradient: "bg-[#FEFAF6]",
  },
];

/* -------------------------------------------------------------------------- */
/*                               WHY CHOOSE US                                */
/* -------------------------------------------------------------------------- */

export const whyChooseCards = [
  {
    icon: "/images/crm/why-1.png",
    title: "Custom Solutions",
    description: "Tailored app development to meet your unique business needs.",
  },
  {
    icon: "/images/crm/why-2.png",
    title: "Seamless User Experience",
    description: "Intuitive design for effortless navigation and interaction.",
  },
  {
    icon: "/images/crm/why-3.png",
    title: "Robust Security",
    description: "Advanced encryption and data protection for user safety.",
  },
  {
    icon: "/images/crm/why-4.png",
    title: "Scalable Architecture",
    description: "Future-ready solutions that grow with your business.",
  },
  {
    icon: "/images/crm/why-5.png",
    title: "24/7 Support",
    description: "Dedicated assistance to keep your app running smoothly.",
  },
];

/* -------------------------------------------------------------------------- */
/*                               TECHNOLOGIES                                 */
/* -------------------------------------------------------------------------- */

export type TechTab = {
  id: string;
  label: string;
  items: { name: string; Icon: IconType; color?: string }[];
};

export const techTabs: TechTab[] = [
  {
    id: "frameworks",
    label: "Frameworks",
    items: [
      { name: "Flutter", Icon: SiFlutter, color: "text-[#02569B]" },
      { name: "Flutter SDK", Icon: SiFlutter, color: "text-[#42A5F5]" },
      { name: "Dart SDK", Icon: SiDart, color: "text-[#0175C2]" },
    ],
  },
  {
    id: "tools",
    label: "Tools & Utilities",
    items: [
      { name: "Android Studio", Icon: SiAndroidstudio, color: "text-[#3DDC84]" },
      { name: "Visual Studio", Icon: DiVisualstudio, color: "text-[#5C2D91]" },
      { name: "Git", Icon: SiGit, color: "text-[#F05032]" },
      { name: "Git Lab", Icon: SiGitlab, color: "text-[#FC6D26]" },
      { name: "Bit Bucket", Icon: SiBitbucket, color: "text-[#0052CC]" },
    ],
  },
  {
    id: "database",
    label: "Database",
    items: [
      { name: "Firebase", Icon: SiFirebase, color: "text-[#FFCA28]" },
      { name: "MongoDB", Icon: SiMongodb, color: "text-[#47A248]" },
      { name: "PostgreSQL", Icon: SiPostgresql, color: "text-[#4169E1]" },
      { name: "SQLite", Icon: SiSqlite, color: "text-[#003B57]" },
    ],
  },
  {
    id: "cicd",
    label: "CI/CD Tools",
    items: [
      { name: "Travis", Icon: SiTravisci, color: "text-[#3EAAAF]" },
      { name: "Cirrus", Icon: SiCirrusci, color: "text-[#212121]" },
      { name: "Codemagic", Icon: SiCodemagic, color: "text-[#F45E3F]" },
    ],
  },
  {
    id: "testing",
    label: "Testing",
    items: [
      { name: "Selenium", Icon: SiSelenium, color: "text-[#43B02A]" },
      { name: "Postman", Icon: SiPostman, color: "text-[#FF6C37]" },
      { name: "Test Rail", Icon: SiTestrail, color: "text-[#65C179]" },
      { name: "Apache JMeter", Icon: SiApachejmeter, color: "text-[#D22128]" },
    ],
  },
  {
    id: "cloud",
    label: "Cloud",
    items: [
      { name: "AWS", Icon: FaAws, color: "text-[#FF9900]" },
      { name: "Azure", Icon: VscAzure, color: "text-[#0089D6]" },
    ],
  },
];

/* -------------------------------------------------------------------------- */
/*                             LARAVEL SOLUTIONS                              */
/* -------------------------------------------------------------------------- */

export type LaravelSolution = {
  title: string;
  description: string;
  benefits: string[];
  image: string;
  alt: string;
};
export const laravelSolutions: LaravelSolution[] = [
  {
    title: "Laravel with Vue JS",
    description:
      "Hire Laravel experts who are well-versed with VueJs to make powerful web apps with beautiful UI designs.",
    benefits: [
      "Faster Page loading time",
      "SEO-Friendly designs",
      "High Performance",
    ],
    image: "/images/appointment/vue.png",
    alt: "Laravel Vue JS",
  },
  {
    title: "Laravel with HTML",
    description:
      "Develop full-stack Laravel solutions using the benefits of Laravel + HTML by Hiring Dedicated Laravel Developers.",
    benefits: [
      "Attractive Frontend Designs",
      "Flexible & Scalable Solutions",
      "Easy Traffic Handling",
    ],
    image: "/images/icons/html.png",
    alt: "Laravel HTML",
  },
  {
    title: "Laravel with React",
    description:
      "Hire Dedicated Laravel Developers to create fast-performing front-ends & Restful APIs using Laravel + React.",
    benefits: [
      "Friendly User Experience",
      "Quick Loading",
      "Easy to Maintain",
    ],
    image: "/images/icons/react-js.png",
    alt: "Laravel React",
  },
];